import { GRAPH_DEFAULTS, LABEL_VALUES, SCALES } from "../Enums/index.js";
import { formatNumberForBar } from "../Scales/scaleNumberFormatter.js";
import isAtTick from "./isAtTick.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { boxedLabel } from "./boxedLabelConfiguration.js";
import { labelIntersectsWithXLine } from "./utils.js";

const shortenToFit = (stackName, maxWidth, context = null) => {
	let contextProvide = context;

	if (!context) {
		const canvas = document.createElement("canvas");
		context = canvas.getContext("2d");
		context.font = "11px Arial";
	}

	const metrics = context.measureText(stackName);

	if (metrics.width <= maxWidth || stackName === "") {
		return stackName + (contextProvide ? ".." : "");
	}

	return shortenToFit(stackName.slice(0, -1), maxWidth, context);
};

export const legendValueLabel = (
	derivedDocument: DocumentDO,
	isDarkMode: boolean
) => {
	return {
		align: "center",
		anchor: "center",
		...boxedLabel(derivedDocument, isDarkMode),
		clamp: true,
		formatter: function (value, ctx) {
			const labelValue = derivedDocument.label_value;
			const chart = ctx.chart;

			const totalKey =
				ctx.dataset.yAxisID === SCALES.Y_SECONDARY
					? SCALES.Y_SECONDARY
					: SCALES.Y;

			const stackTotal =
				chart.$totalizer.stacktotal[totalKey][
					`${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-${ctx.dataset.motivesType}`
				]?.[ctx.dataIndex] ??
				0 +
					(chart.$totalizer.stacktotal[totalKey]?.[
						`${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-residualCapacity`
					]?.[ctx.dataIndex] ?? 0);

			const meta = chart.getDatasetMeta(ctx.datasetIndex);
			const model = meta.data[ctx.dataIndex];
			if (!model) return null;
			const { width } = model.getProps(["width"], true);

			switch (labelValue) {
				case LABEL_VALUES.CATEGORY: {
					return shortenToFit(ctx.dataset.label, Math.max(0, width - 20));
				}
				case LABEL_VALUES.PERCENTAGE: {
					if (value === 0) {
						return null;
					}
					return formatNumberForBar(
						"percentage",
						derivedDocument.label_scale_format,
						value / stackTotal,
						width
					);
				}
				case LABEL_VALUES.PERCENTAGE_OF_TOTAL: {
					if (value === 0) {
						return null;
					}
					let total = 0;
					if (ctx.dataset.yAxisID === SCALES.Y) {
						total =
							chart.$totalizer[`graphYTotal_${ctx.dataset.mirrorDirection}`] +
							(chart.$totalizer[`graphYResidualCapacityTotal`] ?? 0);
					}
					if (ctx.dataset.yAxisID === SCALES.Y_SECONDARY) {
						total = chart.$totalizer.graphYSecondaryTotal;
					}
					if (ctx.dataset.yAxisID === SCALES.Y_MIN) {
						total = chart.$totalizer.graphYMinTotal;
					}
					if (ctx.dataset.yAxisID === SCALES.Y_MAX) {
						total = chart.$totalizer.graphYMaxTotal;
					}
					if (ctx.dataset.yAxisID === SCALES.Y_CAPACITY) {
						total = Math.round(ctx.chart.$totalizer.graphYCapacityTotal);
					}

					return formatNumberForBar(
						"percentage",
						derivedDocument.label_scale_format,
						value / total,
						width
					);
				}
				default: {
					if (
						derivedDocument.formatForGraph === "percentage" ||
						derivedDocument.is_visualised_as_percentage_share
					) {
						if (value === 0) {
							return null; // todo: check if we want to show 0.0%
						}
						if (derivedDocument.is_visualised_as_percentage_share) {
							return formatNumberForBar(
								"percentage",
								derivedDocument.label_scale_format,
								value / 100,
								width
							);
						}

						return formatNumberForBar(
							"percentage",
							derivedDocument.label_scale_format,
							value,
							width
						);
					}

					if (value < -0.9 || value > 0.9) {
						return formatNumberForBar(
							derivedDocument.formatForGraph!,
							derivedDocument.label_scale_format,
							value,
							width
						);
					} else {
						return null;
					}
				}
			}
		},
		font: function () {
			return {
				size: 12,
			};
		},
		color: function () {
			return GRAPH_DEFAULTS.TEXT_COLOR;
		},
		display: function (ctx) {
			// Disable for secondary vertical.
			if (ctx.dataset.yAxisID === SCALES.Y_SECONDARY) {
				return false;
			}

			if (
				!(
					derivedDocument.is_label_visible &&
					ctx.dataset.yAxisID !== SCALES.Y_CAPACITY &&
					isAtTick(ctx)
				)
			) {
				return false;
			}

			return labelIntersectsWithXLine(ctx);
		},
	};
};
